<div data-testid="vendor-page" class="p-6 flex-table-page">
  <!-- Header -->
  <div class="page-header flex-table-header">
    <h2 class="page-title">
      {{ "vendor.management.title" | translate }}
    </h2>
  </div>
  <!-- Search Form -->
  <app-search-form
    data-testid="vendor-search-form"
    [searchFormat]="searchFormConfig"
    (formSearch)="onSearch($event)"
    (formReset)="onReset()"
  >
    <div slot="actions" class="flex gap-2">
      <app-button
        [testId]="'vendor-column-config-btn'"
        type="success"
        [outlined]="true"
        icon="view_column"
        (emitOnClick)="onColumnConfigClick($event)"
      ></app-button>
      <app-status-filter
        [primaryButton]="primaryButton()"
        (primaryButtonClick)="onAdd()"
      ></app-status-filter>
      @if (canCreate() || canApprove()) {
        <p-menu
          data-testid="vendor-template-menu"
          #templateMenu
          [popup]="true"
          [model]="templateMenuItems()"
          appendTo="body"
        ></p-menu>
        <app-button
          [testId]="'vendor-download-template-btn'"
          type="secondary"
          icon="download"
          [label]="'vendor.download_template.title' | translate"
          (emitOnClick)="templateMenu.toggle($event)"
        ></app-button>
        <p-menu
          data-testid="vendor-import-menu"
          #importMenu
          [popup]="true"
          [model]="importMenuItems()"
          appendTo="body"
        ></p-menu>
        <app-button
          [testId]="'vendor-import-btn'"
          type="info"
          icon="upload"
          [label]="'vendor.import.title' | translate"
          (emitOnClick)="importMenu.toggle($event)"
        ></app-button>
        <input
          #vendorSeedFileInput
          data-testid="vendor-seed-file-input"
          type="file"
          accept=".csv,text/csv"
          class="hidden"
          (change)="onVendorSeedFileSelected($event)"
        />
        <input
          #vendorXrefFileInput
          data-testid="vendor-xref-file-input"
          type="file"
          accept=".csv,text/csv"
          class="hidden"
          (change)="onVendorXrefFileSelected($event)"
        />
      }
    </div>
  </app-search-form>

  <!-- Data Table -->
  <div class="flex-table-section">
    <advanced-data-table
      data-testid="vendor-table"
      #advancedTable
      [dataSource]="vendors()"
      [defaultColumns]="defaultColumns()"
      [flexibleColumns]="flexibleColumns()"
      [loading]="loading()"
      [rows]="searchParams().size"
      [first]="(searchParams().page - 1) * searchParams().size"
      [totalRecords]="totalRecords()"
      scrollHeight="flex"
      [showInternalColumnConfigButton]="false"
      class="w-full"
      (pageChange)="onPageChange($event)"
    >
    <!-- Custom Template for Date Columns -->
    <ng-template
      #customTemplate
      let-value
      let-column="column"
      let-rowData="rowData"
    >
      @if (column?.prop === "creationDate") {
        <span class="text-xs font-medium" style="font-size: 12px">
          {{ formatDate(value) }}
        </span>
      }
      @if (column?.prop === "status") {
        <div class="flex items-center justify-center gap-1 w-full">
          <p-tag
            data-testid="vendor-status-tag"
            [value]="getStatusDisplayText(value)"
            [severity]="getStatusSeverity(value)"
            styleClass="status-tag"
            [ngStyle]="getStatusStyle(value)"
            [pTooltip]="getStatusDisplayText(value)"
            tooltipPosition="top"
            tooltipStyleClass="custom-tooltip"
            [showDelay]="300"
            [hideDelay]="0"
          />
        </div>
      }
      @if (column?.prop === "vendorType") {
        <span class="text-xs text-gray-700">
          {{ getVendorTypeDisplayText(value) }}
        </span>
      }
      @if (column?.prop === "country") {
        <span class="text-xs text-gray-700">
          {{ getCountryDisplayText(value) }}
        </span>
      }
      @if (column?.prop === "paymentTerm") {
        <span class="text-xs text-gray-700">
          {{ getPaymentTermDisplayText(value) }}
        </span>
      }
      @if (column?.prop === "paymentMethod") {
        <span class="text-xs text-gray-700">
          {{ getPaymentMethodDisplayText(value) }}
        </span>
      }
      @if (column?.prop === "riskLevel") {
        <span class="text-xs text-gray-700">
          {{ getRiskLevelDisplayText(value) }}
        </span>
      }
    </ng-template>

    <!-- Custom Status Template -->
    <ng-template #statusTemplate let-status>
      <div class="flex items-center justify-center gap-1 w-full">
        <p-tag
          data-testid="vendor-status-tag-template"
          [value]="getStatusDisplayText(status)"
          [severity]="getStatusSeverity(status)"
          styleClass="status-tag"
          [ngStyle]="getStatusStyle(status)"
          [pTooltip]="getStatusDisplayText(status)"
          tooltipPosition="top"
          tooltipStyleClass="custom-tooltip"
          [showDelay]="300"
          [hideDelay]="0"
        />
      </div>
    </ng-template>

    <!-- Custom Actions Template -->
    <ng-template #actionsTemplate let-rowData>
      <div class="inline-flex items-center gap-2 whitespace-nowrap">
        <!-- View button -->
        @if (canRead()) {
          <span
            [attr.data-testid]="'vendor-action-view-' + rowData.id"
            (click)="onActionClick('view', rowData)"
            class="action-link action-view cursor-pointer"
          >
            {{ "common.actions.view" | translate }}
          </span>
        }

        <!-- Others button - only visible if there are actions available and user has permissions -->
        @if (
          getVendorActions(rowData).length > 0 &&
          hasEnabledVendorActions(rowData)
        ) {
          <button
            [attr.data-testid]="'vendor-action-others-' + rowData.id"
            type="button"
            class="action-link action-others cursor-pointer"
            aria-haspopup="menu"
            (click)="toggleMenu($event, rowData)"
          >
            {{ "common.actions.others" | translate }}
          </button>
        }
      </div>
    </ng-template>
    </advanced-data-table>
  </div>
</div>

<!-- Single Menu for All Actions -->
<p-menu
  data-testid="vendor-actions-menu"
  #actionMenu
  [popup]="true"
  [model]="currentMenuModel()"
  appendTo="body"
  [baseZIndex]="2100"
  styleClass="actions-menu text-[0.75rem]"
  [autoZIndex]="true"
></p-menu>

<!-- Toast Messages -->
<p-toast data-testid="vendor-toast"></p-toast>

<!-- Shared Confirmation Dialog -->
<app-confirmation-dialog
  [testId]="'vendor-confirmation-dialog'"
  [(visible)]="showConfirmationDialog"
  [title]="dialogTitle"
  [message]="dialogMessage"
  [buttons]="dialogButtons"
  (emitOnClick)="onConfirmatiButtonClick($event)"
></app-confirmation-dialog>
